/**
 * Zod схемы валидации для KLS (Классификаторы и Справочники)
 * 
 * Используются для валидации форм создания и редактирования
 * сущностей KLS в административной панели.
 */

import { z } from "zod";

/**
 * Валидация ltree пути (рубрика)
 * Формат: строка с точками, например "1.2.3" или "a.b.c"
 */
export const ltreeSchema = z.string().regex(
  /^[a-zA-Z0-9_]+(\.[a-zA-Z0-9_]+)*$/,
  "Рубрика должна быть в формате ltree (например: 1.2.3 или a.b.c)"
);

/**
 * Схема валидации для типа квалификатора (QualType)
 */
export const qualTypeSchema = z.object({
  qual_type_namef: z.string().min(1, "Полное наименование обязательно"),
  qual_type_names: z.string().optional(),
  qual_type_code: z.string().optional(),
  qual_type_vers: z.number().int().positive().default(1),
});

export type QualTypeFormData = z.infer<typeof qualTypeSchema>;

/**
 * Схема валидации для квалификатора (Qual)
 */
export const qualSchema = z.object({
  qual_type_id: z.number().int().positive("Необходимо выбрать тип квалификатора"),
  qual_namef: z.string().min(1, "Полное наименование обязательно"),
  qual_names: z.string().optional(),
  qual_note: z.string().optional(),
  qual_code: z.string().min(1, "Код справочника обязателен"),
  qual_vers: z.number().int().positive().default(1),
});

export type QualFormData = z.infer<typeof qualSchema>;

/**
 * Схема валидации для раздела классификатора (Kls)
 */
export const klsSchema = z.object({
  qual_id: z.number().int().positive("Необходимо выбрать квалификатор"),
  kls_namef: z.string().min(1, "Полное наименование обязательно"),
  kls_names: z.string().optional(),
  kls_note: z.string().optional(),
  kls_code: z.string().min(1, "Код раздела обязателен"),
  kls_vers: z.number().int().positive().default(1),
  kls_rubrika: ltreeSchema,
});

export type KlsFormData = z.infer<typeof klsSchema>;

/**
 * Схема валидации для внешнего источника (KlsExternalSources)
 */
export const externalSourceSchema = z.object({
  name: z.string().min(1, "Название обязательно").max(255),
  description: z.string().optional(),
  source_type: z.enum(["postgresql", "mysql", "mssql", "oracle", "file", "api"], {
    errorMap: () => ({ message: "Необходимо выбрать тип источника" }),
  }),
  connection_string: z.string().min(1, "Строка подключения обязательна"),
  schema_name: z.string().max(100).optional(),
  table_mapping: z.record(z.any()).optional().default({}),
  sync_schedule: z.string().max(100).optional(),
  auto_sync: z.boolean().default(false),
  sync_mode: z.enum(["full", "incremental"]).default("incremental"),
  is_active: z.boolean().default(true),
});

export type ExternalSourceFormData = z.infer<typeof externalSourceSchema>;

/**
 * Схема валидации для cron выражения (расписание синхронизации)
 * Базовая валидация формата cron
 */
export const cronSchema = z.string().regex(
  /^(\*|([0-9]|[1-5][0-9])|\*\/([0-9]|[1-5][0-9])) (\*|([0-9]|1[0-9]|2[0-3])|\*\/([0-9]|1[0-9]|2[0-3])) (\*|([1-9]|[12][0-9]|3[01])|\*\/([1-9]|[12][0-9]|3[01])) (\*|([1-9]|1[0-2])|\*\/([1-9]|1[0-2])) (\*|([0-6])|\*\/([0-6]))$/,
  "Некорректный формат cron выражения (например: 0 0 * * *)"
).optional();

/**
 * Схема валидации для JSON маппинга таблиц
 */
export const tableMappingSchema = z.record(z.any()).refine(
  (data) => {
    try {
      JSON.stringify(data);
      return true;
    } catch {
      return false;
    }
  },
  {
    message: "Некорректный формат JSON",
  }
).optional().default({});

